import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Star, User, Quote } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface RatingWithReviewer {
  id: string;
  rating: number;
  written_feedback: string | null;
  created_at: string;
  reviewer_name: string;
  reviewer_user_type: string;
  job_title: string;
}

interface TradesmanRatingsDialogProps {
  tradesmanId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const TradesmanRatingsDialog = ({ tradesmanId, open, onOpenChange }: TradesmanRatingsDialogProps) => {
  const [ratings, setRatings] = useState<RatingWithReviewer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [tradesmanInfo, setTradesmanInfo] = useState<{
    company_name: string;
    average_rating: number;
    total_reviews: number;
  } | null>(null);

  useEffect(() => {
    if (open && tradesmanId) {
      fetchRatings();
      fetchTradesmanInfo();
    }
  }, [open, tradesmanId]);

  const fetchTradesmanInfo = async () => {
    if (!tradesmanId) return;

    try {
      const { data } = await supabase.rpc("get_public_tradesman_profile", { 
        tradesman_id: tradesmanId 
      });
      
      const profile = Array.isArray(data) ? data[0] : data;
      if (profile) {
        setTradesmanInfo({
          company_name: profile.company_name || "Tradesman",
          average_rating: profile.average_rating || 0,
          total_reviews: profile.total_reviews || 0
        });
      }
    } catch (error) {
      console.error("Error fetching tradesman info:", error);
    }
  };

  const fetchRatings = async () => {
    if (!tradesmanId) return;

    setIsLoading(true);
    try {
      // Use SECURITY DEFINER RPC that returns denormalized public-safe rating data
      const { data: ratingsData, error } = await supabase
        .rpc("get_public_ratings", { p_reviewee_id: tradesmanId });

      if (error) throw error;

      const mapped = (ratingsData || []).map((r: any) => ({
        id: r.id,
        rating: r.rating,
        written_feedback: r.written_feedback,
        created_at: r.created_at,
        job_id: r.job_id,
        reviewer_id: r.reviewer_id,
        reviewer_name: r.reviewer_name || "Anonymous",
        reviewer_user_type: r.reviewer_user_type || "customer",
        job_title: r.job_title || "Job",
      }));
      setRatings(mapped);
    } catch (error) {
      console.error("Error fetching ratings:", error);
      setRatings([]);
    } finally {
      setIsLoading(false);
    }
  };


  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating ? "text-yellow-500 fill-current" : "text-gray-300"
            }`}
          />
        ))}
      </div>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="h-5 w-5 text-yellow-500" />
            Customer Reviews
          </DialogTitle>
        </DialogHeader>

        {tradesmanInfo && (
          <div className="bg-muted/50 rounded-lg p-4 mb-4">
            <h3 className="font-semibold text-lg mb-2">{tradesmanInfo.company_name}</h3>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                {renderStars(Math.round(tradesmanInfo.average_rating))}
                <span className="font-medium">{tradesmanInfo.average_rating.toFixed(1)}</span>
              </div>
              <Badge variant="outline">
                {tradesmanInfo.total_reviews} review{tradesmanInfo.total_reviews !== 1 ? 's' : ''}
              </Badge>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-sm text-muted-foreground">Loading reviews...</p>
          </div>
        ) : ratings.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Star className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No customer reviews yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {ratings.map((rating) => (
              <div key={rating.id} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <User className="h-8 w-8 text-muted-foreground bg-muted rounded-full p-1" />
                    <div>
                      <p className="font-medium">{rating.reviewer_name}</p>
                      <p className="text-sm text-muted-foreground">
                        Job: {rating.job_title}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    {renderStars(rating.rating)}
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatDate(rating.created_at)}
                    </p>
                  </div>
                </div>

                {rating.written_feedback && (
                  <div className="bg-muted/30 rounded-lg p-3 relative">
                    <Quote className="h-4 w-4 text-muted-foreground absolute top-2 left-2" />
                    <p className="text-sm pl-6 italic">{rating.written_feedback}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};